import React from 'react'
// libs
import styled from 'styled-components'
import { useSnackbar } from 'notistack'
// themes
import { APP_HEADER_HEIGHT, colors } from 'themes'
// utils
import { transparentize } from 'polished'
import { getSnackbarCloseButton } from 'providers/SnackbarProvider/utils'
// constants
import { Modals, ENTITIES } from 'constants/index'
// context
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API/index'
// hooks
import { useShowModal } from 'hooks/index'
// graphql
import { PLAN_ACTION_DETAILS_QUERY } from 'graphql/planActions/queries'
import { PLAN_KPI_DETAILS_QUERY } from 'graphql/planKPIs/queries'
import { PLAN_OBJECTIVE_DETAILS_QUERY } from 'graphql/planObjectives/queries'
// types
import { IPlanActionsQueryArgs, IPlanKpIsQueryArgs, IPlanObjectivesQueryArgs } from 'types/graphql'

type Props = React.PropsWithChildren<unknown>

// styled
export const LinkingModeFocuser = styled.div`
  position: fixed;
  height: ${APP_HEADER_HEIGHT};
  top: 0;
  left: 0;
  right: 0;
  z-index: 1300;
  background: ${transparentize(0.5, colors.black)};
`

// types
declare namespace NLinkingMode {
  export interface IContext {
    handleSetIsOpen: (value: boolean) => void
    isLinkingMode: boolean
    enableLinkingMode: THandleEnableLinkingMode
    linkingSource: ILinkingSource
    refetchQuery: {
      query: any
      variables: IPlanObjectivesQueryArgs | IPlanActionsQueryArgs | IPlanKpIsQueryArgs
    }[]
  }

  export type THandleDisableLinkingMode = () => void
  export type THandleEnableLinkingMode = (
    sourceId: string,
    sourcePlanId: string,
    sourceTitle: string,
    sourceEntityType: string
  ) => void

  interface ILinkingSource {
    id: string
    entityType: string
    planId: string
  }
}

export const LinkingModeContext = React.createContext<NLinkingMode.IContext>({} as any)

const LinkingModeAPI: React.FC<Props> = ({ children }) => {
  // state
  const [isLinkingMode, setIsLinkingMode] = React.useState<boolean>(false)
  const [linkingSource, setLinkingSource] = React.useState<NLinkingMode.ILinkingSource & any>({})
  const [open, setOpen] = React.useState<boolean>(false)

  const { planDetailsRefetch, treeQueryRefetch } = React.useContext(PlanEditPageContext)

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const { hideModal, showModal } = useShowModal({ modalType: Modals.LINKING_SOURCE_DETAILS_MODAL })

  // methods
  const handleSetIsOpen = (value) => setOpen(value)

  const enableLinkingMode: NLinkingMode.THandleEnableLinkingMode = async (
    sourceId,
    sourcePlanId,
    sourceTitle,
    sourceEntityType
  ) => {
    await treeQueryRefetch()

    setLinkingSource({ id: sourceId, entityType: sourceEntityType, planId: sourcePlanId })
    setIsLinkingMode(true)

    showModal({ id: sourceId, planId: sourcePlanId, title: sourceTitle, width: open ? '664px' : '600px' })

    enqueueSnackbar('"Link" to an item below or "Create & Link" to new!', {
      variant: 'info',
      action: (key) => getSnackbarCloseButton(key, closeSnackbar, 'DONE', disableLinkingMode),
      autoHideDuration: null
    })
  }

  const disableLinkingMode: NLinkingMode.THandleDisableLinkingMode = async () => {
    await treeQueryRefetch()

    setIsLinkingMode(false)
    setLinkingSource({})

    await planDetailsRefetch()

    hideModal()
  }

  const getQuery = () => {
    if (linkingSource.entityType === ENTITIES.PLAN_ACTION) {
      return PLAN_ACTION_DETAILS_QUERY
    }
    if (linkingSource.entityType === ENTITIES.PLAN_KPI) {
      return PLAN_KPI_DETAILS_QUERY
    }
    if (linkingSource.entityType === ENTITIES.PLAN_OBJECTIVE) {
      return PLAN_OBJECTIVE_DETAILS_QUERY
    }
  }

  const refetchQuery = [
    {
      query: getQuery(),
      variables: {
        filter: {
          ids: [linkingSource.id],
          planId: linkingSource.planId
        }
      }
    }
  ]

  const value = {
    handleSetIsOpen,
    enableLinkingMode,
    isLinkingMode,
    linkingSource,
    refetchQuery
  } as any

  return <LinkingModeContext.Provider value={value}>{children}</LinkingModeContext.Provider>
}

export default LinkingModeAPI
